import { Tab } from "@chakra-ui/tabs";
import { keepPreviousData } from "@tanstack/react-query";
import { DistributionDeleteArgs } from "features/capital-activity/event/CapitalEventTypes";
import { FundFamilyDistributionPageTitle } from "features/distributions/FundFamilyDistributionPage";
import {
  getCapitalActivityEventRetrieveQueryKey,
  getCapitalActivityInvestorOverviewRetrieveQueryKey,
  getCapitalActivitySummaryRetrieveQueryKey,
} from "gen/endpoints/capital-activity/capital-activity";
import {
  getFundFamilyDistributionRetrieveQueryKey,
  useDistributionBreakdownTableDataRetrieve,
  useDistributionDelete,
  useDistributionUnfundedBreakdownTableDataRetrieve,
} from "gen/endpoints/distributions/distributions";
import {
  getFundFamilyEventRetrieveQueryKey,
  getFundFamilySubEventFirstRetrieveQueryKey,
  getFundFamilySubEventListQueryKey,
  getNoticeDocumentGenerationStatusRetrieveQueryKey,
  getWaterfallRunListQueryKey,
  useFundFamilyEventRetrieveSuspense,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { noop } from "lodash-es";
import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { LoadingState } from "shared/components/empty/LoadingState";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import {
  PageWizardFormFooter,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { DeleteConfirmationDialog } from "shared/components/modal/DeleteConfirmationDialog";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const FundFamilyDistributionBreakdownTable: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
}> = ({ fundFamilyId, fundFamilyEventId }) => {
  const { t } = useTranslation(["common", "distribution"]);

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const {
    data: distributionBreakdownTableData,
    isFetching: isBreakdownLoading,
  } = useDistributionBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const {
    data: distributionUnfundedBreakdownTableData,
    isFetching: isUnfundedBreakdownLoading,
  } = useDistributionUnfundedBreakdownTableDataRetrieve(
    fundFamilyId,
    fundFamilyEventId,
    {
      is_equalized_view: isEqualizedView || undefined,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const selectedTabId: "distribution" | "unfunded" =
    selectedTab === 0 ? "distribution" : "unfunded";

  const tableData =
    selectedTabId === "distribution"
      ? distributionBreakdownTableData
      : distributionUnfundedBreakdownTableData;

  return (
    <TableBox isFullWidth={true} key={selectedTabId}>
      <TableBoxHeader>
        <TableBoxTabs
          index={selectedTab}
          onChange={setSelectedTab}
          colorScheme="gray"
        >
          <Tab>
            {t("distribution:distributionBreakdown.distributionSummary")}
          </Tab>
          <Tab>{t("distribution:distributionBreakdown.unfunded")}</Tab>
        </TableBoxTabs>
        <DataTableBoxActions>
          <EqualizedViewToggleButton
            isEqualizedView={isEqualizedView}
            setIsEqualizedView={setIsEqualizedView}
          />
        </DataTableBoxActions>
      </TableBoxHeader>
      <TableBoxBody>
        {tableData ? (
          <DataTable
            data={tableData}
            isLoading={
              selectedTabId === "distribution"
                ? isBreakdownLoading
                : isUnfundedBreakdownLoading
            }
          />
        ) : (
          <LoadingState />
        )}
      </TableBoxBody>
    </TableBox>
  );
};

const FundFamilyDistributionBreakdownForm: React.FC<{
  fundFamilyId: string;
  fundFamilyEventId: string;
}> = ({ fundFamilyId, fundFamilyEventId }) => {
  const { navigateNext } = usePageWizard();

  const form = useForm();
  return (
    <Form form={form} onSubmit={noop} onSuccess={navigateNext}>
      <FundFamilyDistributionBreakdownTable
        fundFamilyId={fundFamilyId}
        fundFamilyEventId={fundFamilyEventId}
      />
      <PageWizardFormFooter />
    </Form>
  );
};

export const FundFamilyDistributionBreakdownPage: React.FC = () => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "distribution", "capitalActivity"]);
  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  return (
    <>
      <FundFamilyDistributionPageTitle
        title={t("distribution:investorBreakdown.title")}
        eventData={eventData}
        showAdjustments={true}
      />
      <PageBody>
        <FundFamilyDistributionBreakdownForm
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
        />
      </PageBody>
    </>
  );
};

export const FundFamilyDistributionOverviewPage: React.FC = () => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();
  const { t } = useTranslation(["common", "distribution", "capitalActivity"]);

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );
  const invalidateQueryKeys = useInvalidateQueryKeys();

  const invalidateQueries = useCallback(
    () =>
      invalidateQueryKeys(
        getFundFamilyEventRetrieveQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityEventRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilySubEventListQueryKey(fundFamilyId, fundFamilyEventId),
        getWaterfallRunListQueryKey(fundFamilyId, fundFamilyEventId),
        getCapitalActivityInvestorOverviewRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getNoticeDocumentGenerationStatusRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getFundFamilyDistributionRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
        ),
        getCapitalActivitySummaryRetrieveQueryKey(fundFamilyId),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            type: "waterfall",
            raise_if_none: false,
          },
        ),
        getFundFamilySubEventFirstRetrieveQueryKey(
          fundFamilyId,
          fundFamilyEventId,
          {
            raise_if_none: false,
          },
        ),
      ),
    [fundFamilyId, fundFamilyEventId, invalidateQueryKeys],
  );

  const { mutateAsync: deleteDistribution } = useDistributionDelete({
    mutation: {
      onSuccess: async () => await invalidateQueries(),
    },
  });
  const navigate = useNavigate();

  const confirmDistributionDelete = useConfirmationDialog(
    DeleteConfirmationDialog as DeleteConfirmationDialog<DistributionDeleteArgs>,
    {
      title: t("capitalActivity:distributionDeleteDialog.title"),
      description: t("capitalActivity:distributionDeleteDialog.description"),
      successMessage: t("capitalActivity:distributionDeleteDialog.success"),
      performDelete: deleteDistribution,
    },
  );

  const onDelete = async () => {
    await confirmDistributionDelete({ fundFamilyId, fundFamilyEventId });
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId }).$,
    );
  };

  const onEdit = () =>
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .capitalActivity({})
        .capitalActivityEvent({ fundFamilyEventId })
        .distributions({})
        .capitalActivityEventDistributions({}).$,
    );

  return (
    <>
      <FundFamilyDistributionPageTitle
        title={t("distribution:header.title")}
        eventData={eventData}
        onDelete={onDelete}
        onEdit={onEdit}
        backButton={{
          title: t("common:draftCapitalActivity"),
          path: rootRoute({})
            .fundFamilies({})
            .fundFamily({ fundFamilyId })
            .capitalActivity({})
            .capitalActivityEvent({ fundFamilyEventId }).$,
        }}
        showAdjustments={true}
      />
      <PageBody>
        <FundFamilyDistributionBreakdownTable
          fundFamilyId={fundFamilyId}
          fundFamilyEventId={fundFamilyEventId}
        />
      </PageBody>
    </>
  );
};
